import { useState } from "react";
import { IonCheckbox, IonItem, IonList } from "@ionic/react";
import { Button, LoadingInterstitial } from "swing-components";
import { shallowEqualArrays } from "swing-utils";
import { translate } from "utils/string-table";

import { useSchools } from "~components/FormFields";
import { SharedModalHeader } from "~components/Molecules";

import { ThreadedData } from "./types";

type SchoolsModalProps = Pick<ThreadedData, "ls" | "setLs"> & { onDismiss: () => void };

/**
 * Multi-select of the schools this admin manages, opened from its own pill in
 * the filter bar. Selection is held in temporary form state and only committed
 * on Apply, so ticking boxes does not refetch the index on every tap.
 */
export function SchoolsModal(props: SchoolsModalProps) {
  const { ls, setLs, onDismiss } = props;

  const [tempSchools, setTempSchools] = useState<Array<string>>(ls.schools);

  const schools = useSchools();

  if (!schools)
    return (
      <div
        css={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <LoadingInterstitial />
      </div>
    );

  const isClearButtonEnabled = tempSchools.length > 0;
  const isApplyButtonEnabled = !shallowEqualArrays(ls.schools, tempSchools);

  return (
    <div
      css={{
        position: "relative",
        overflow: "scroll",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <SharedModalHeader title={translate("LABEL_SCHOOLS")} onDismiss={onDismiss} />
      <div css={{ padding: "16px", paddingTop: "var(--ion-safe-area-top)", flex: 1 }}>
        <p>{translate("SCHOOLS_MODAL_DESCRIPTION")}</p>
        <IonList>
          {schools.map((item, index) => (
            <IonItem key={item.id} lines={index === schools.length - 1 ? "none" : undefined}>
              <IonCheckbox
                labelPlacement="end"
                justify="start"
                checked={tempSchools.includes(item.id)}
                value={item.id}
                onClick={(e) => {
                  const schoolId = e.currentTarget.value;
                  setTempSchools((old) =>
                    old.includes(schoolId) ? old.filter((s) => s !== schoolId) : [...old, schoolId],
                  );
                }}
              >
                {item.name}
              </IonCheckbox>
            </IonItem>
          ))}
        </IonList>
      </div>
      <div
        css={{
          position: "sticky",
          bottom: 0,
          padding: "16px",
          background: "white",
          display: "flex",
          gap: 16,
        }}
      >
        <div css={{ flex: 1 }}>
          <Button
            type="tertiary"
            status={isClearButtonEnabled ? "enabled" : "disabled"}
            onClick={() => setTempSchools([])}
            isFluid
          >
            {translate("FILTERS_MODAL_CLEAR_BUTTON")}
          </Button>
        </div>
        <div css={{ flex: 1 }}>
          <Button
            status={isApplyButtonEnabled ? "enabled" : "disabled"}
            onClick={() => {
              setLs((old) => ({ ...old, schools: tempSchools }));
              onDismiss();
            }}
            type="primary"
            isFluid
          >
            {translate("FILTERS_MODAL_APPLY_BUTTON")}
          </Button>
        </div>
      </div>
    </div>
  );
}
